import { useState } from "react"
import { useDraggable } from "@dnd-kit/core"
import { EllipsisVerticalIcon, GripVerticalIcon } from "lucide-react"
import { EditableLabel } from "@/components/editable-text/editable-label"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { ImageDialog } from "./imagedialog"
import { ItemIssuesButton } from "./issues"
import { ItemSettingsDialog } from "./itemsettingsdialog"
import { slugify } from "./text"

interface Props {
  item: PlaybookItem
  vars: string[]
  index: number
  issues: PlaybookIssue[]
  onUpdate: (patch: Partial<PlaybookItem>) => void
  onRemove: () => void
  icon?: string
  children: React.ReactNode
}

function isEmptyItem(item: PlaybookItem) {
  return !item.title && !item.say && !item.prompt && !item.context && !item.intent && !item.conditions?.length && !item.options?.length && !item.transitions?.length
    && !item.references?.length && !item.captures?.length && !item.tools?.length && !item.set && !item.on_no_match?.say && !item.image
}

export function ItemShell({ item, vars, index, issues, onUpdate, onRemove, icon, children }: Props) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: String(index) })
  const [imageOpen, setImageOpen] = useState(false)

  function handleTitleChange(title: string) {
    onUpdate(item.id.trim() ? { title } : { title, id: slugify(title) })
  }

  return <div ref={setNodeRef} className={cn("rounded-lg border border-gray-400 p-3 flex flex-col gap-2 shadow-md hover:shadow-lg transition-shadow", isDragging && "opacity-50")}>
    <div className="flex items-center gap-2">
      <span {...attributes} {...listeners} className="cursor-grab touch-none text-muted-foreground"><GripVerticalIcon className="size-4" /></span>
      {icon && <span className="text-2xl leading-none">{icon}</span>}
      <EditableLabel value={item.title ?? ""} onChange={handleTitleChange} placeholder="Untitled" className="text-lg font-semibold text-foreground flex-1" />
      {item.image && <button type="button" aria-label="Item image" onClick={() => setImageOpen(true)} className="size-8 shrink-0 overflow-hidden rounded border">
        <img src={item.image} alt="" className="size-full object-cover" />
      </button>}
      <ItemIssuesButton issues={issues} />
      <ItemMenu item={item} vars={vars} onUpdate={onUpdate} onRemove={onRemove} onImage={() => setImageOpen(true)} />
    </div>
    {children}
    {item.type !== "ai" && <ImageDialog open={imageOpen} onOpenChange={setImageOpen} image={item.image} onChange={image => onUpdate({ image })} />}
  </div>
}

function ItemMenu({ item, vars, onUpdate, onRemove, onImage }: { item: PlaybookItem, vars: string[], onUpdate: (patch: Partial<PlaybookItem>) => void, onRemove: () => void, onImage: () => void }) {
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const active = (item.conditions?.length ?? 0) > 0 || !!item.intent || !!item.classify
  const has = (type: PlaybookToolConfig["type"]) => !!item.tools?.some(t => t.type === type)
  const addTool = (tool: PlaybookToolConfig) => onUpdate({ tools: [...(item.tools ?? []), tool] })

  return <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label="Item menu" className={cn(active && "text-blue-700 font-bold")}>
          <EllipsisVerticalIcon className="size-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {item.type !== "ai" && <DropdownMenuItem onClick={() => onUpdate({ options: [...(item.options ?? []), { label: "" }] })}>Add option</DropdownMenuItem>}
        {item.type !== "ai" && <DropdownMenuItem onClick={onImage}>{item.image ? "Image..." : "Add image"}</DropdownMenuItem>}
        {item.type === "ai" && <DropdownMenuItem onClick={() => onUpdate({ transitions: [...(item.transitions ?? []), { description: "", action: { type: "next" } }] })}>Add transition</DropdownMenuItem>}
        {item.type === "ai" && <DropdownMenuItem onClick={() => onUpdate({ references: [...(item.references ?? []), { description: "", text: "" }] })}>Add reference</DropdownMenuItem>}
        {item.type === "ai" && <DropdownMenuSub>
          <DropdownMenuSubTrigger>Add tool</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuItem onClick={() => onUpdate({ captures: [...(item.captures ?? []), { var: "", type: "text", description: "" }] })}>Capture</DropdownMenuItem>
            <DropdownMenuItem onClick={() => addTool({ type: "search", description: "", sources: [] })}>Search</DropdownMenuItem>
            <DropdownMenuItem onClick={() => addTool({ type: "generate", description: "", prompt: "" })}>Generator</DropdownMenuItem>
            <DropdownMenuItem onClick={() => addTool({ type: "pdf", description: "" })}>PDF</DropdownMenuItem>
            <DropdownMenuItem disabled={has("notes")} onClick={() => addTool({ type: "notes" })}>Notepad</DropdownMenuItem>
            <DropdownMenuItem disabled={has("calculator")} onClick={() => addTool({ type: "calculator" })}>Calculator</DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => setSettingsOpen(true)}>Settings</DropdownMenuItem>
        <DropdownMenuItem onClick={() => isEmptyItem(item) ? onRemove() : setConfirmOpen(true)}>Remove</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <ItemSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} item={item} vars={vars} onUpdate={onUpdate} />
    <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove this item?</AlertDialogTitle>
          <AlertDialogDescription>"{item.title || "Untitled item"}" will be removed from the flow.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onRemove}>Remove</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
}
